// 🔧 dashboard.js
// ✅ Purpose: Main landing page after login
// ✅ Features:
// - Shows user's managed events and pending event requests
// - Shows only event names + ballot links for events user has voted in
// - Allows requesting a new event via a form
// - Displays manager confirmation status from session

import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth_context";
import { auth, db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  getDoc,
  doc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import FullPageSpinner from "../../components/FullPageSpinner";
import {
  VOTING_TYPE_OPTIONS,
  DEFAULT_VOTING_TYPE,
} from "../../utils/voting_types";
import { logger } from '../../utils/logger';
import { showStatus } from "../../utils/status_helpers";
import {
  SESSION_KEYS,
  getSessionValue,
  removeSessionValue,
} from "../../utils/browser_storage_helpers";
import SeoHead from "../../components/seo_head";

const Dashboard = () => {

  const { user, authLoading } = useAuth();
  const [managerEvents, setManagerEvents] = useState([]);
  const [votedEvents, setVotedEvents] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [status, setStatus] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [emailVerified, setEmailVerified] = useState(false);
  const [requestForm, setRequestForm] = useState({
    name: "",
    startDate: "",
    endDate: "",
    marketingUrl: "",
    description: "",
    votingType: DEFAULT_VOTING_TYPE,
  });

  useEffect(() => {
    const refreshVerificationStatus = async () => {
      if (!auth.currentUser) {
        setEmailVerified(false);
        return;
      }

      try {
        await auth.currentUser.reload();
        await auth.currentUser.getIdToken(true);

        setEmailVerified(auth.currentUser.emailVerified === true);

        logger.log("📩 Dashboard email verification status refreshed:", {
          emailVerified: auth.currentUser.emailVerified,
        });
      } catch (err) {
        logger.error("❌ Failed to refresh email verification status:", {
          value: err,
        });

        setEmailVerified(user?.emailVerified === true);
      }
    };

    if (!authLoading && user?.uid) {
      refreshVerificationStatus();
    }
  }, [authLoading, user]);

  const loadData = async () => {
    if (!user?.uid) {
      logger.warn("⚠️ Cannot load data — user UID missing", {});
      return;
    }
    
    try {
      // 🔹 Load managed events
      const [eventsSnap, userSnap] = await Promise.all([
        getDocs(query(collection(db, "events"), where("managerIds", "array-contains", user.uid))),
        getDoc(doc(db, "users", user.uid)),
      ]);
  
      const managerList = eventsSnap.docs
        .map((doc) => {
          const data = doc.data();
          return {
            slug: data.slug,
            name: data.name || data.slug || doc.id,
            pending: data.pending === true,
          };
        })
        .filter((e) => !e.pending);
      setManagerEvents(managerList);
  
      // 🔹 Load only voted events
      const votedIds = userSnap.exists()
        ? userSnap.data().votedEvents || []
        : [];

      const votedEventsData = [];

      for (const eventId of votedIds) {
        try {
          const eventSnap = await getDoc(
            doc(db, "events", eventId)
          );

          if (eventSnap.exists()) {
            const data = eventSnap.data();

            votedEventsData.push({
              id: eventId,
              name: data.name || eventId,
              slug: data.slug || eventId,
            });
          }
        } catch (err) {
          logger.warn("⚠️ Failed loading voted event:", {
            value: eventId,
          });
        }
      }

      setVotedEvents(votedEventsData);

      // 🔹 Load any pending event requests this user has submitted
      const pendingSnap = await getDocs(
        query(collection(db, "pendingEvents"), where("requester.email", "==", user.email))
      );

      const updatedPending = pendingSnap.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,

          name: data.name,

          startDate: data.startDate?.toDate(),

          selectedPlanName:
            data.selectedPlanName ||
            "Unknown Plan",

          paymentStatus:
            data.paymentStatus ||
            "not_paid",

          approvalStatus:
            data.approvalStatus ||
            data.reviewStatus ||
            "pending",
        };
      });
      setPendingRequests(updatedPending);
    } catch (err) {

      logger.error("❌ Error loading event data:", {
        value: err
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load your event information.",
      });

    }
  };

  useEffect(() => {
    if (!authLoading && user?.uid) {
      loadData();
    }
  }, [authLoading, user]);

  useEffect(() => {
    if (!user) return;

    const acceptedManagerEvent = getSessionValue(
      SESSION_KEYS.MANAGER_ACCEPTED_EVENT_NAME
    );

    if (!acceptedManagerEvent) return;

    showStatus(setStatus, {
      type: "success",
      message: `You are now the Let's Go Vote manager for ${acceptedManagerEvent}.`,
    });

    removeSessionValue(
      SESSION_KEYS.MANAGER_ACCEPTED_EVENT_NAME
    );
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRequestForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    const {
      name,
      startDate,
      endDate,
      marketingUrl,
      description,
      votingType,
    } = requestForm;

    try {
      const newPendingEvent = {
        name,
        description,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        marketingUrl,
        pending: true,
        archived: false,
        createdAt: serverTimestamp(),
        requester: {
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
        },
        votingType: votingType || DEFAULT_VOTING_TYPE,
      };

      await addDoc(collection(db, "pendingEvents"), newPendingEvent);

      setRequestForm({
        name: "",
        startDate: "",
        endDate: "",
        marketingUrl: "",
        description: "",
        votingType: DEFAULT_VOTING_TYPE,
      });
      setShowRequestForm(false);

      showStatus(setStatus, {
        type: "success",
        message: "Your event request has been submitted for review. We'll contact you soon!",
      });

      await loadData();
    } catch (err) {

      logger.error("❌ Failed to submit request:", {
        value: err
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to submit. Please try again later.",
      });

    }
  };

  if (authLoading) return <FullPageSpinner message="🔐 Loading user..." />;
  if (!user) {
    logger.warn("🔐 User not logged in — redirecting to login", {});
    window.location.href = `/login?redirect=${encodeURIComponent("/dashboard")}`;
    return null;
  }
  
  return (
  <>
  <SeoHead
      title="Dashboard | Let's Go Vote"
      noindex={true}
    />
  <section id="top-content" className="container-fluid py-5">
    <div className="row mb-4">
      <div className="col text-start">
        <h1 className="display-5">👋 Welcome, {user.firstName || "friend"}!</h1>                   
        <p className="lead text-muted">Start by scanning your first QR code.</p>

      </div>
    </div>

    <div className="white-section p-4 rounded shadow-sm bg-white">
      {/* 📨 Request Form Section */}
      <section className="mb-5">

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {!emailVerified && (
          <div className="alert alert-warning text-start">
            <p className="mb-2">
            <strong>📩 Almost done!</strong>
            <br />
            Check your email for a message from Let's Go Vote. Then click the verification link in the email to start voting.
            </p>

            {getSessionValue(SESSION_KEYS.PENDING_VOTE_CAR_ID) && getSessionValue(SESSION_KEYS.LAST_EVENT_SLUG) && (
              <p className="mb-2">
                🗳️ Your scanned vote is saved. After email verification, we’ll send you back to finish it.
              </p>
            )}

            <p className="mb-2">
            <br />
            Didn't get an email from us? Check your spam folder. You can also resend the email.
            </p>

            <Link to="/resend_verification" className="btn btn-warning">
              🔁 Resend Verification Email
            </Link>
          </div>
        )}

        <Link to="/request_event" className="btn btn-outline-primary mb-3">
          📬 Request to use Let's Go Vote at your own event
        </Link>

        {emailVerified && showRequestForm && (
          <form
            onSubmit={handleRequestSubmit}
            className="border rounded p-4 mb-4 bg-light"
          >
            <h4 className="mb-3">📝 Submit a New Event</h4>

            <div className="mb-3">
              <label className="form-label">Event Name*</label>
              <input
                name="name"
                className="form-control"
                value={requestForm.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Start Date*</label>
                <input
                  type="date"
                  name="startDate"
                  className="form-control"
                  value={requestForm.startDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">End Date*</label>
                <input
                  type="date"
                  name="endDate"
                  className="form-control"
                  value={requestForm.endDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label">Voting Type*</label>
              <select
                name="votingType"
                className="form-select"
                value={requestForm.votingType || DEFAULT_VOTING_TYPE}
                onChange={handleChange}
                required
              >
                {VOTING_TYPE_OPTIONS.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <div className="form-text">
                Choose how voting should work for this event. This is reviewed during approval.
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label">Description *</label>
              <textarea
                name="description"
                className="form-control"
                rows={3}
                value={requestForm.description}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mt-3">
              <label className="form-label">Marketing URL (optional)</label>
              <input
                name="marketingUrl"
                className="form-control"
                value={requestForm.marketingUrl}
                onChange={handleChange}
              />
            </div>

            <button type="submit" className="btn btn-primary mt-3">
              📤 Submit for Review
            </button>
          </form>
        )}
      </section>

      {/* 🧑‍💼 Manager View */}
      {(managerEvents.length > 0 || pendingRequests.length > 0) && (
        <section className="mb-5">
          <h3 className="mb-3">
            🏁 My Events
          </h3>
          <ul className="list-group">
            {managerEvents.map((e) => (
              <li
                key={e.slug || e.id}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{e.name}</strong>

                  <div className="small text-muted">
                    Event Manager
                  </div>
                </div>
                <Link
                  to={`/${e.slug}/`}
                  className="btn btn-sm btn-outline-primary"
                >
                  Open Event
                </Link>
              </li>
            ))}
            
            {pendingRequests.map((e) => (
              <li
                key={e.id}
                className="list-group-item"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{e.name}</strong>

                    <div className="small text-muted">
                      {e.selectedPlanName}
                    </div>

                    <div className="small mt-1">

                      {e.paymentStatus === "paid" ? (
                        <span className="badge bg-success me-2">
                          Payment Received
                        </span>
                      ) : (
                        <span className="badge bg-warning text-dark me-2">
                          Payment Pending
                        </span>
                      )}

                      {e.approvalStatus === "approved" ? (
                        <span className="badge bg-success">
                          Approved
                        </span>
                      ) : (
                        <span className="badge bg-secondary">
                          Awaiting Approval
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    {e.paymentStatus === "paid" ? (
                      <span className="btn btn-sm btn-outline-success disabled">
                        ✅ Paid
                      </span>
                    ) : (
                      <span className="btn btn-sm btn-outline-warning disabled">
                        💳 Awaiting Payment
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 🗳 Voted Events */}

      {emailVerified ? (
        <section className="mb-5">
          <h3 className="mb-3">🗳 My Ballots</h3>
          {votedEvents.length === 0 ? (
            <p className="text-muted">You haven't voted yet. Start scanning now!</p>
          ) : (
            <ul className="list-group">
              {votedEvents.map((e) => (
                <li
                  key={e.id}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  {e.name}
                  <Link
                    to={`/${e.slug}/ballot`}
                    className="btn btn-sm btn-outline-secondary"
                  >
                    View Ballot
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <section className="mb-5">
          <div className="alert alert-secondary text-start">
            Verify your email to view events you have voted in.
          </div>
        </section>
      )}

      {/* =====================================================
          🗳️ How Voting Works
      ===================================================== */}
      <div className="alert alert-info text-start mb-4">
        <h5 className="mb-3">🗳️ How to Vote</h5>

        <ol className="mb-0">
          <li>
            📸 Scan the QR code on a car, auction item, or event entry.
          </li>

          <li>
            ✅ Review the entry and submit your vote.
          </li>

          <li>
            🔁 Repeat for your favorite entries throughout the event.
          </li>

          <li>
            🗂️ You may update or change your votes at any time while voting is open.
          </li>

          <li>
            🏆 View results on the leaderboard once event organizers enable it.
          </li>
        </ol>
      </div>

    </div>
  </section>
  </>
  );
};

export default Dashboard;
